/**
 * Share card component - creates a styled card for sharing with cube progress bar
 */

import { LifeStats, formatNumber, formatPercent, formatCompactNumber } from '../utils/calculations';

export interface ShareCardOptions {
    stats: LifeStats;
    quote?: {
        text: string;
        author: string;
    };
}

/**
 * Create a share card element optimized for sharing
 * Includes: header, key stats, cube-styled progress bar, all 4 reflective stats, and branding
 */
export function createShareCard(options: ShareCardOptions): HTMLElement {
    const { stats, quote } = options;

    const card = document.createElement('div');
    card.className = 'share-card';

    // Header with age
    const header = document.createElement('div');
    header.className = 'share-card-header';
    header.innerHTML = `
        <h2 class="share-card-title">My Life in Weeks</h2>
        <p class="share-card-subtitle">${stats.ageYears} years, ${stats.ageMonths} months old</p>
    `;
    card.appendChild(header);

    // Stats row
    const statsRow = document.createElement('div');
    statsRow.className = 'share-card-stats';
    statsRow.innerHTML = `
        <div class="share-stat">
            <span class="share-stat-value accent">${formatPercent(stats.percentLived)}</span>
            <span class="share-stat-label">of life lived</span>
        </div>
        <div class="share-stat">
            <span class="share-stat-value">${formatNumber(stats.weeksLived)}</span>
            <span class="share-stat-label">weeks lived</span>
        </div>
        <div class="share-stat">
            <span class="share-stat-value">${formatNumber(stats.weeksRemaining)}</span>
            <span class="share-stat-label">weeks remaining</span>
        </div>
    `;
    card.appendChild(statsRow);

    // Cube-styled progress bar (small cubes forming a bar)
    const cubeContainer = document.createElement('div');
    cubeContainer.className = 'share-card-cube-progress';

    const canvas = document.createElement('canvas');
    const canvasWidth = 260;
    const canvasHeight = 20;
    canvas.width = canvasWidth * 2; // 2x for retina
    canvas.height = canvasHeight * 2;
    canvas.style.width = `${canvasWidth}px`;
    canvas.style.height = `${canvasHeight}px`;

    const ctx = canvas.getContext('2d')!;
    ctx.scale(2, 2);

    // Draw cube progress bar
    drawCubeProgressBar(ctx, canvasWidth, canvasHeight, stats);

    cubeContainer.appendChild(canvas);
    card.appendChild(cubeContainer);

    // All 4 reflective stats in a grid
    const reflectiveSection = document.createElement('div');
    reflectiveSection.className = 'share-card-reflective-grid';
    reflectiveSection.innerHTML = `
        <div class="share-reflective-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M12 21C12 21 4 14 4 8.5C4 5.42 6.42 3 9.5 3C11.24 3 12.91 3.81 14 5.09C15.09 3.81 16.76 3 18.5 3C21.58 3 24 5.42 24 8.5C24 14 16 21 16 21" fill="var(--accent)" opacity="0.2"/>
                <path d="M19.5 12.572L12 20l-7.5-7.428A5 5 0 1 1 12 6.006a5 5 0 1 1 7.5 6.572"/>
            </svg>
            <span class="share-reflective-value">${formatCompactNumber(stats.heartbeats)}</span>
            <span class="share-reflective-label">heartbeats</span>
        </div>
        <div class="share-reflective-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <path d="M12 22c-4-3-8-6-8-10a8 8 0 0 1 16 0c0 4-4 7-8 10Z" fill="var(--accent)" opacity="0.15"/>
                <path d="M12 2v4m0 4v4m-4-6h8"/>
            </svg>
            <span class="share-reflective-value">${formatCompactNumber(stats.breaths)}</span>
            <span class="share-reflective-label">breaths</span>
        </div>
        <div class="share-reflective-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <circle cx="12" cy="17" r="5" fill="var(--accent)" opacity="0.3"/>
                <path d="M12 2v2m0 18v-4m-7.07-7.07l1.41 1.41m12.73 0l1.41-1.41M2 17h4m12 0h4"/>
                <line x1="4" y1="22" x2="20" y2="22"/>
            </svg>
            <span class="share-reflective-value">${formatNumber(stats.sunrises)}</span>
            <span class="share-reflective-label">sunrises</span>
        </div>
        <div class="share-reflective-item">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                <circle cx="12" cy="12" r="9" fill="rgba(255,255,255,0.8)"/>
                <circle cx="12" cy="12" r="9"/>
            </svg>
            <span class="share-reflective-value">${formatNumber(stats.fullMoons)}</span>
            <span class="share-reflective-label">full moons</span>
        </div>
    `;
    card.appendChild(reflectiveSection);

    // Optional quote
    if (quote) {
        const quoteSection = document.createElement('div');
        quoteSection.className = 'share-card-quote';
        quoteSection.innerHTML = `
            <p class="quote-text">${quote.text}</p>
            <p class="quote-author">— ${quote.author}</p>
        `;
        card.appendChild(quoteSection);
    }

    // Branding footer
    const footer = document.createElement('div');
    footer.className = 'share-card-footer';
    footer.innerHTML = `
        <span class="share-brand">Made with</span>
        <span class="share-brand-name">Finite</span>
        <span class="share-brand-divider">•</span>
        <span class="share-brand-url">finite.eleventh-photon.com</span>
    `;
    card.appendChild(footer);

    return card;
}

/**
 * Draw cube-styled progress bar - small cubes forming a horizontal bar
 */
function drawCubeProgressBar(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    stats: LifeStats
): void {
    const cubeSize = 8;
    const cubeSpacing = 1;
    const totalCubes = Math.floor(width / (cubeSize + cubeSpacing));
    const livedCubes = Math.round(totalCubes * (stats.percentLived / 100));
    const currentCube = livedCubes;

    const startY = height / 2;

    for (let i = 0; i < totalCubes; i++) {
        const x = i * (cubeSize + cubeSpacing) + cubeSize / 2;
        const isLived = i < livedCubes;
        const isCurrent = i === currentCube;

        drawIsometricCube(ctx, x, startY, cubeSize * 0.9, isLived, isCurrent);
    }
}

/**
 * Draw a single isometric cube
 */
function drawIsometricCube(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    isLived: boolean,
    isCurrent: boolean
): void {
    const h = size * 0.5;

    let topColor: string;
    let leftColor: string;
    let rightColor: string;

    if (isCurrent) {
        topColor = '#10B981';
        leftColor = '#34D399';
        rightColor = '#6EE7B7';
    } else if (isLived) {
        topColor = '#F59E0B';
        leftColor = '#FBBF24';
        rightColor = '#FCD34D';
    } else {
        topColor = 'rgba(26, 26, 26, 0.06)';
        leftColor = 'rgba(26, 26, 26, 0.08)';
        rightColor = 'rgba(26, 26, 26, 0.04)';
    }

    // Top face
    ctx.fillStyle = topColor;
    ctx.beginPath();
    ctx.moveTo(x, y - h);
    ctx.lineTo(x + size * 0.866, y - h / 2);
    ctx.lineTo(x, y);
    ctx.lineTo(x - size * 0.866, y - h / 2);
    ctx.closePath();
    ctx.fill();

    // Left face
    ctx.fillStyle = leftColor;
    ctx.beginPath();
    ctx.moveTo(x - size * 0.866, y - h / 2);
    ctx.lineTo(x, y);
    ctx.lineTo(x, y + h);
    ctx.lineTo(x - size * 0.866, y + h / 2);
    ctx.closePath();
    ctx.fill();

    // Right face
    ctx.fillStyle = rightColor;
    ctx.beginPath();
    ctx.moveTo(x + size * 0.866, y - h / 2);
    ctx.lineTo(x, y);
    ctx.lineTo(x, y + h);
    ctx.lineTo(x + size * 0.866, y + h / 2);
    ctx.closePath();
    ctx.fill();
}
